import React, { useState, useRef, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useUserIdentity } from "../contexts/UserIdentityContext";
import { Pharmacy } from "../utils/supabase";
import GlobalSearch from "./GlobalSearch";
import NotificationDropdown from "./NotificationDropdown";
import { useNavBadges } from "../hooks/useNavBadges";
import {
  Bars3Icon,
  XMarkIcon,
  HomeIcon,
  ClipboardDocumentListIcon,
  ChatBubbleLeftRightIcon,
  ScaleIcon,
  CalendarDaysIcon,
  ExclamationTriangleIcon,
  CogIcon,
  ArrowRightOnRectangleIcon,
  BuildingStorefrontIcon,
  UserIcon,
  UsersIcon,
  ArrowPathIcon,
  BeakerIcon,
  MagnifyingGlassIcon,
  QueueListIcon,
  DocumentTextIcon,
  FolderOpenIcon,
  ChartBarIcon,
  ArrowTrendingUpIcon,
  ArrowTopRightOnSquareIcon,
  AcademicCapIcon,
} from "@heroicons/react/24/outline";
import {
  HomeIcon as HomeIconSolid,
  ClipboardDocumentListIcon as TasksIconSolid,
  ChatBubbleLeftRightIcon as ChatIconSolid,
  ScaleIcon as ScaleIconSolid,
  CalendarDaysIcon as CalendarIconSolid,
  ExclamationTriangleIcon as ExclamationIconSolid,
  CogIcon as CogIconSolid,
  UsersIcon as UsersIconSolid,
  BeakerIcon as BeakerIconSolid,
  QueueListIcon as QueueListIconSolid,
  DocumentTextIcon as DocumentTextIconSolid,
  FolderOpenIcon as FolderOpenIconSolid,
  ChartBarIcon as ChartBarIconSolid,
  AcademicCapIcon as AcademicCapIconSolid,
} from "@heroicons/react/24/solid";

interface LayoutProps {
  children: React.ReactNode;
  currentPage: string;
  onPageChange: (page: string) => void;
}

const Layout: React.FC<LayoutProps> = ({
  children,
  currentPage,
  onPageChange,
}) => {
  const {
    user,
    selectedPharmacy,
    signOut,
    pharmacies,
    selectPharmacy,
    forceRefresh,
  } = useAuth();
  const { identifiedUser, clearIdentity } = useUserIdentity();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [pharmacySelectorOpen, setPharmacySelectorOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const pharmacyDropdownRef = useRef<HTMLDivElement>(null);
  const badges = useNavBadges();

  // Cmd+K / Ctrl+K global search shortcut
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setSearchOpen((o) => !o);
      }
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, []);

  // G: Close pharmacy dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        pharmacyDropdownRef.current &&
        !pharmacyDropdownRef.current.contains(e.target as Node)
      ) {
        setPharmacySelectorOpen(false);
      }
    };
    if (pharmacySelectorOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [pharmacySelectorOpen]);

  const baseNavigation = [
    {
      name: "Dashboard",
      id: "dashboard",
      icon: HomeIcon,
      iconSolid: HomeIconSolid,
      current: currentPage === "dashboard",
      badge: 0,
      dot: false,
    },
    {
      name: "Agenda",
      id: "agenda",
      icon: QueueListIcon,
      iconSolid: QueueListIconSolid,
      current: currentPage === "agenda",
      badge: 0,
      dot: false,
    },
    {
      name: "Taken",
      id: "tasks",
      icon: ClipboardDocumentListIcon,
      iconSolid: TasksIconSolid,
      current: currentPage === "tasks",
      badge: badges.urgentOrOverdueTasks,
      dot: false,
    },
    {
      name: "Communicatie",
      id: "communications",
      icon: ChatBubbleLeftRightIcon,
      iconSolid: ChatIconSolid,
      current: currentPage === "communications",
      badge: badges.unreadComms,
      dot: false,
    },
    {
      name: "Controle balansen",
      id: "calibrations",
      icon: ScaleIcon,
      iconSolid: ScaleIconSolid,
      current: currentPage === "calibrations",
      badge: 0,
      dot: badges.overdueCalibrations,
    },
    {
      name: "Controle vervaldata",
      id: "expiry-checks",
      icon: CalendarDaysIcon,
      iconSolid: CalendarIconSolid,
      current: currentPage === "expiry-checks",
      badge: 0,
      dot: badges.expiredItems,
    },
    {
      name: "Temperatuurcontrole",
      id: "temperature-control",
      icon: ArrowTrendingUpIcon,
      iconSolid: ArrowTrendingUpIcon,
      current: false,
      badge: 0,
      dot: false,
      href: "https://www.saclient.com/nl-nl/",
    },
    {
      name: "Magistrale bereidingen",
      id: "compounding-preparations",
      icon: BeakerIcon,
      iconSolid: BeakerIconSolid,
      current: currentPage === "compounding-preparations",
      badge: 0,
      dot: badges.draftPreparations,
    },
    {
      name: "Klachtenbeheer",
      id: "complaints",
      icon: ExclamationTriangleIcon,
      iconSolid: ExclamationIconSolid,
      current: currentPage === "complaints",
      badge: 0,
      dot: badges.openComplaints,
    },
    {
      name: "Documenten",
      id: "documents",
      icon: FolderOpenIcon,
      iconSolid: FolderOpenIconSolid,
      current: currentPage === "documents",
      badge: 0,
      dot: badges.documentsReviewDue,
    },
    {
      name: "Pharfolio",
      id: "training",
      icon: AcademicCapIcon,
      iconSolid: AcademicCapIconSolid,
      current: false,
      badge: 0,
      dot: false,
      href: "https://www.pharfolio.be/mijn-pharfolio",
    },
    {
      name: "Instellingen",
      id: "settings",
      icon: CogIcon,
      iconSolid: CogIconSolid,
      current: currentPage === "settings",
      badge: 0,
      dot: false,
    },
  ];

  // Add User Management for admin users
  const navigation =
    user?.role === "admin"
      ? [
          ...baseNavigation.slice(0, -1), // All items except Settings
          {
            name: "Gebruikers",
            id: "user-management",
            icon: UsersIcon,
            iconSolid: UsersIconSolid,
            current: currentPage === "user-management",
            badge: 0,
            dot: false,
          },
          {
            name: "QA Rapport",
            id: "qa-report",
            icon: ChartBarIcon,
            iconSolid: ChartBarIconSolid,
            current: currentPage === "qa-report",
            badge: 0,
            dot: false,
          },
          {
            name: "Auditlog",
            id: "audit-log",
            icon: DocumentTextIcon,
            iconSolid: DocumentTextIconSolid,
            current: currentPage === "audit-log",
            badge: 0,
            dot: false,
          },
          baseNavigation[baseNavigation.length - 1], // Settings at the end
        ]
      : baseNavigation;

  const handleSignOut = async () => {
    await signOut();
  };

  const handlePharmacyChange = (pharmacy: Pharmacy) => {
    selectPharmacy(pharmacy);
    setPharmacySelectorOpen(false);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile sidebar */}
      <div
        className={`fixed inset-0 z-40 lg:hidden ${sidebarOpen ? "" : "pointer-events-none"}`}
      >
        <div
          className={`fixed inset-0 bg-gray-600 bg-opacity-75 transition-opacity ${
            sidebarOpen ? "opacity-100" : "opacity-0"
          }`}
          onClick={() => setSidebarOpen(false)}
        />
        <div
          className={`fixed inset-y-0 left-0 flex w-full max-w-xs flex-col bg-white shadow-xl transition-transform ${
            sidebarOpen ? "translate-x-0" : "-translate-x-full"
          }`}
        >
          <div className="flex h-16 items-center justify-between px-4 shadow-sm">
            <div className="flex items-center">
              <img
                src="/logo192.png"
                alt="Met* Logo"
                className="h-8 w-8 rounded"
              />
              <span className="ml-2 text-lg font-semibold text-gray-900">
                Met<sup>*</sup>
              </span>
            </div>
            <button
              type="button"
              className="h-8 w-8 rounded-md bg-white flex items-center justify-center text-gray-500 hover:text-gray-600"
              onClick={() => setSidebarOpen(false)}
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-4 py-4">
            <nav className="space-y-1">
              {navigation.map((item) => {
                const Icon = item.current ? item.iconSolid : item.icon;
                return (
                  <button
                    key={item.name}
                    onClick={() => {
                      if ((item as any).href) {
                        window.open((item as any).href, "_blank", "noopener,noreferrer");
                        setSidebarOpen(false);
                      } else {
                        onPageChange(item.id);
                        setSidebarOpen(false);
                      }
                    }}
                    className={`w-full group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors ${
                      item.current
                        ? "bg-primary-100 text-primary-900"
                        : "text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                    }`}
                  >
                    <Icon
                      className={`mr-3 h-5 w-5 flex-shrink-0 ${
                        item.current
                          ? "text-primary-500"
                          : "text-gray-400 group-hover:text-gray-500"
                      }`}
                    />
                    <span className="flex-1 text-left">{item.name}</span>
                    {(item as any).href && (
                      <ArrowTopRightOnSquareIcon className="ml-1 h-3.5 w-3.5 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                    )}
                    {item.badge > 0 && (
                      <span className="ml-2 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1 text-xs font-bold text-white bg-red-500 rounded-full">
                        {item.badge > 99 ? "99+" : item.badge}
                      </span>
                    )}
                    {item.badge === 0 && item.dot && (
                      <span className="ml-2 h-2 w-2 rounded-full bg-amber-400 flex-shrink-0" />
                    )}
                  </button>
                );
              })}
            </nav>
          </div>

          <div className="border-t border-gray-200 p-4">
            <button
              onClick={handleSignOut}
              className="w-full group flex items-center px-2 py-2 text-sm font-medium text-gray-700 rounded-md hover:bg-gray-100 hover:text-gray-900 transition-colors"
            >
              <ArrowRightOnRectangleIcon className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500" />
              Uitloggen
            </button>
          </div>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <div className="flex flex-col flex-grow bg-white border-r border-gray-200 shadow-sm">
          <div className="flex items-center h-16 px-4 border-b border-gray-200">
            <img
              src="/logo192.png"
              alt="Met* Logo"
              className="h-8 w-8 rounded"
            />
            <span className="ml-2 text-lg font-semibold text-gray-900">
              Met<sup>*</sup>
            </span>
          </div>

          <div className="flex-1 flex flex-col px-4 py-4">
            <nav className="flex-1 space-y-1">
              {navigation.map((item) => {
                const Icon = item.current ? item.iconSolid : item.icon;
                return (
                  <button
                    key={item.name}
                    onClick={() => {
                      if ((item as any).href) {
                        window.open((item as any).href, "_blank", "noopener,noreferrer");
                      } else {
                        onPageChange(item.id);
                      }
                    }}
                    className={`w-full group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors ${
                      item.current
                        ? "bg-primary-100 text-primary-900"
                        : "text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                    }`}
                  >
                    <Icon
                      className={`mr-3 h-5 w-5 flex-shrink-0 ${
                        item.current
                          ? "text-primary-500"
                          : "text-gray-400 group-hover:text-gray-500"
                      }`}
                    />
                    <span className="flex-1 text-left">{item.name}</span>
                    {(item as any).href && (
                      <ArrowTopRightOnSquareIcon className="ml-1 h-3.5 w-3.5 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                    )}
                    {item.badge > 0 && (
                      <span className="ml-2 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1 text-xs font-bold text-white bg-red-500 rounded-full">
                        {item.badge > 99 ? "99+" : item.badge}
                      </span>
                    )}
                    {item.badge === 0 && item.dot && (
                      <span className="ml-2 h-2 w-2 rounded-full bg-amber-400 flex-shrink-0" />
                    )}
                  </button>
                );
              })}
            </nav>
          </div>

          <div className="border-t border-gray-200 p-4">
            <button
              onClick={handleSignOut}
              className="w-full group flex items-center px-2 py-2 text-sm font-medium text-gray-700 rounded-md hover:bg-gray-100 hover:text-gray-900 transition-colors"
            >
              <ArrowRightOnRectangleIcon className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500" />
              Uitloggen
            </button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64 flex flex-col flex-1">
        {/* Top navigation */}
        <div className="sticky top-0 z-30 flex h-16 bg-white border-b border-gray-200 shadow-sm">
          <div className="flex items-center px-4 sm:px-6 lg:px-8 w-full">
            {/* Mobile menu button */}
            <button
              type="button"
              className="lg:hidden -ml-0.5 -mt-0.5 h-12 w-12 inline-flex items-center justify-center rounded-md text-gray-500 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500"
              onClick={() => setSidebarOpen(true)}
            >
              <Bars3Icon className="h-6 w-6" />
            </button>

            {/* Pharmacy selector */}
            <div className="flex-1 flex items-center justify-between">
              <div className="relative" ref={pharmacyDropdownRef}>
                <button
                  type="button"
                  onClick={() => setPharmacySelectorOpen(!pharmacySelectorOpen)}
                  className="flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white text-sm leading-4 font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  <BuildingStorefrontIcon className="h-4 w-4 mr-2 text-gray-400" />
                  <span className="truncate max-w-xs">
                    {selectedPharmacy?.name}
                  </span>
                  <svg
                    className="ml-2 h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>

                {pharmacySelectorOpen && (
                  <div className="absolute z-50 mt-1 w-80 bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
                    {pharmacies.map((pharmacy) => (
                      <button
                        key={pharmacy.id}
                        onClick={() => handlePharmacyChange(pharmacy)}
                        className={`w-full text-left relative cursor-pointer select-none py-2 pl-3 pr-9 hover:bg-primary-50 ${
                          selectedPharmacy?.id === pharmacy.id
                            ? "bg-primary-50"
                            : ""
                        }`}
                      >
                        <div className="flex items-center">
                          <BuildingStorefrontIcon className="h-4 w-4 text-primary-600 mr-2" />
                          <div>
                            <div className="font-medium text-gray-900">
                              {pharmacy.name}
                            </div>
                            <div className="text-sm text-gray-500 truncate">
                              {pharmacy.address}
                            </div>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* User menu */}
              <div className="flex items-center space-x-3">
                {/* Global search button */}
                <button
                  type="button"
                  onClick={() => setSearchOpen(true)}
                  className="hidden sm:flex items-center gap-2 px-3 py-1.5 text-sm text-gray-400 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500"
                  title="Zoeken (⌘K)"
                >
                  <MagnifyingGlassIcon className="h-4 w-4" />
                  <span className="text-gray-400">Zoeken…</span>
                  <kbd className="ml-1 text-xs text-gray-300 bg-white border border-gray-200 px-1 py-0.5 rounded hidden lg:inline">
                    ⌘K
                  </kbd>
                </button>
                <button
                  type="button"
                  onClick={() => setSearchOpen(true)}
                  className="sm:hidden p-1 rounded-full text-gray-400 hover:text-gray-500 focus:outline-none"
                  title="Zoeken"
                >
                  <MagnifyingGlassIcon className="h-5 w-5" />
                </button>

                {/* Notification bell */}
                <NotificationDropdown
                  onNavigate={onPageChange}
                  unreadCount={badges.unreadComms}
                />

                {/* Switch user button */}
                <button
                  type="button"
                  onClick={() => clearIdentity()}
                  className="p-1 rounded-full text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  title="Wisselen van gebruiker"
                >
                  <ArrowPathIcon className="h-5 w-5" />
                </button>

                <div className="flex items-center">
                  <div className="h-8 w-8 rounded-full flex items-center justify-center bg-primary-100">
                    <UserIcon className="h-4 w-4 text-primary-600" />
                  </div>
                  <div className="ml-2 hidden sm:block">
                    <span className="text-sm font-medium text-gray-700">
                      {user?.full_name}
                    </span>
                    {identifiedUser && (
                      <div className="text-xs text-primary-600">
                        ID: {identifiedUser.twoDigitId}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="flex-1 overflow-auto">{children}</main>
      </div>

      {/* Global Search Modal */}
      <GlobalSearch
        isOpen={searchOpen}
        onClose={() => setSearchOpen(false)}
        onNavigate={(page) => {
          onPageChange(page);
          setSearchOpen(false);
        }}
      />
    </div>
  );
};

export default Layout;
